import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { Star, MapPin, Scissors, Calendar, Clock, Loader2 } from 'lucide-react';
import { CalendarPicker } from './CalendarPicker';
import { cn } from '../../lib/utils';
import { UserProfile, Service, PortfolioItem } from '../../types';
import { dbService } from '../../services/dbService';

interface BarberDetailsProps {
  barber: UserProfile;
  services: Service[];
  onBooking: (service: Service, date: string, time: string, price: number) => void;
}

export const BarberDetails = ({ barber, services, onBooking }: BarberDetailsProps) => {
  const barberServices = (barber.services && barber.services.length > 0) ? barber.services : services;
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>(barber.portfolio || []);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(!barber.portfolio || barber.portfolio.length === 0);

  useEffect(() => {
    if (barber.uid) {
      dbService.getPortfolio(barber.uid).then(items => {
        if (items && items.length > 0) {
          setPortfolio(items as PortfolioItem[]);
        }
        setIsLoadingPortfolio(false);
      });
    }
  }, [barber.uid]);

  const times = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "19:00", "20:00"];

  const handleBooking = () => {
    if (!selectedService || !selectedTime) return;
    const finalPrice = (barber.services && barber.services.length > 0)
      ? selectedService.price
      : ((selectedService.name.toLowerCase().includes('cukur') || selectedService.name.toLowerCase().includes('cut')) && barber.price > 0)
        ? barber.price
        : selectedService.price;
    onBooking(selectedService, format(selectedDate, 'yyyy-MM-dd'), selectedTime, finalPrice);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          {/* Header */}
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <img src={barber.avatar} className="w-40 h-40 rounded-3xl object-cover shadow-2xl" alt={barber.name} />
            <div className="flex-1 pt-2">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">{barber.name}</h1>
              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-emerald-500 fill-emerald-500" />
                  <span className="font-bold text-slate-900">{barber.rating || '4.9'}</span>
                </div>
                <div className="w-1.5 h-1.5 bg-slate-200 rounded-full" />
                <span className={cn(
                  "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm",
                  barber.status === 'Sibuk' ? "bg-amber-50 text-amber-600 border border-amber-100" :
                  barber.status === 'Non-Aktif' ? "bg-slate-50 text-slate-500 border border-slate-100" :
                  "bg-emerald-50 text-emerald-600 border border-emerald-100"
                )}>
                  {barber.status || 'Tersedia'}
                </span>
                {(barber.province || barber.city || barber.district) && (
                  <>
                    <div className="w-1.5 h-1.5 bg-slate-200 rounded-full" />
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-bold">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{[barber.province, barber.city, barber.district].filter(Boolean).join(', ')}</span>
                    </div>
                  </>
                )}
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">{barber.bio || 'Kapster berpengalaman.'}</p>
            </div>
          </div>

          {/* Services */}
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Scissors className="w-5 h-5 text-emerald-500" />
              Pilih Layanan
            </h2>
            <div className="grid gap-4">
              {barberServices.map((s: Service) => (
                <div 
                  key={s.id}
                  onClick={() => setSelectedService(s)}
                  className={cn(
                    "p-5 rounded-3xl border-2 transition-all cursor-pointer flex items-center justify-between group",
                    selectedService?.id === s.id 
                      ? "border-emerald-500 bg-emerald-50/50 shadow-lg shadow-emerald-500/5" 
                      : "border-slate-100 bg-white hover:border-slate-300"
                  )}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-2xl flex items-center justify-center transition-colors",
                      selectedService?.id === s.id ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                    )}>
                      <Scissors className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.duration} Menit</p>
                    </div>
                  </div>
                  <p className="font-bold text-emerald-600">
                    Rp {(barber.services && barber.services.length > 0)
                      ? s.price.toLocaleString()
                      : ((s.name.toLowerCase().includes('cukur') || s.name.toLowerCase().includes('cut')) && barber.price > 0) 
                        ? barber.price.toLocaleString() 
                        : s.price.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-emerald-500" />
              Pilih Tanggal
            </h2>
            <CalendarPicker selectedDate={selectedDate} onDateSelect={setSelectedDate} />
          </div>

          {/* Portfolio */}
          {(isLoadingPortfolio || portfolio.length > 0) && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Scissors className="w-5 h-5 text-emerald-500" />
                Portofolio Karya
              </h2>
              {isLoadingPortfolio ? (
                <div className="flex justify-center py-12 bg-slate-50 rounded-[2rem]">
                  <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {portfolio.map((item) => (
                    <div key={item.id} className="group relative aspect-square rounded-[2rem] overflow-hidden border border-slate-100 shadow-sm">
                      <img 
                        src={item.imageUrl} 
                        alt={item.title || 'Portfolio Item'} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-6">
                        {item.title && <p className="text-white font-black uppercase text-xs tracking-widest">{item.title}</p>}
                        {item.description && <p className="text-white/80 text-[10px] mt-1 line-clamp-2">{item.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-6 mt-12 flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-500" />
              Pilih Waktu ({format(selectedDate, 'd MMM yyyy', { locale: id })})
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {times.map(t => (
                <button
                  key={t}
                  onClick={() => setSelectedTime(t)}
                  className={cn(
                    "py-3.5 rounded-2xl text-sm font-bold border-2 transition-all active:scale-95",
                    selectedTime === t 
                      ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/30" 
                      : "bg-white border-slate-100 text-slate-600 hover:border-slate-300"
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Bar Logic / Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white p-8 rounded-[2rem] shadow-2xl shadow-slate-200/50 border border-slate-100 sticky top-24">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Ringkasan Pesanan</h3>
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center py-4 border-b border-slate-100">
                <span className="text-slate-500 text-sm">Layanan</span>
                <span className="font-bold text-slate-900 text-sm">{selectedService?.name || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-4 border-b border-slate-100">
                <span className="text-slate-500 text-sm">Kapster</span>
                <span className="font-bold text-slate-900 text-sm">{barber.name}</span>
              </div>
              <div className="flex justify-between items-center py-4 border-b border-slate-100">
                <span className="text-slate-500 text-sm">Tanggal</span>
                <span className="font-bold text-slate-900 text-sm">{format(selectedDate, 'd MMM yyyy', { locale: id })}</span>
              </div>
              <div className="flex justify-between items-center py-4 border-b border-slate-100">
                <span className="text-slate-500 text-sm">Waktu</span>
                <span className="font-bold text-slate-900 text-sm">{selectedTime || '-'}</span>
              </div>
              <div className="flex justify-between items-center pt-4">
                <span className="text-slate-900 font-bold">Total Harga</span>
                <span className="text-2xl font-bold text-emerald-600">
                  Rp {(selectedService 
                    ? (barber.services && barber.services.length > 0)
                      ? selectedService.price
                      : ((selectedService.name.toLowerCase().includes('cukur') || selectedService.name.toLowerCase().includes('cut')) && barber.price > 0)
                        ? barber.price
                        : selectedService.price
                    : 0).toLocaleString()}
                </span>
              </div>
            </div>

            <button
              disabled={!selectedService || !selectedTime}
              onClick={handleBooking}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 disabled:cursor-not-allowed text-white py-5 rounded-3xl font-bold transition-all shadow-xl shadow-emerald-500/20 active:scale-95"
            >
              Konfirmasi Booking
            </button>
            <p className="text-center text-[10px] text-slate-400 mt-4 px-6 italic">
              Dengan mengklik tombol, Anda menyetujui syarat dan ketentuan CukurJe.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
